// Match Card Component
// Simplified single-card view for Matches tab - photo-only with overlay info

import React from "react";
import { Heart, X } from "lucide-react";
import { FeedItem } from "../../../types/socialFeed";
import { signalsService, SignalSurface } from "../../../services/SocialFeed/signalsService";
import {
  MatchCardContainer,
  MatchCardPhoto,
  PhotoOverlay,
  MatchCardName,
  MatchCardInfo,
  MatchCardBadge,
  MatchCardRightActions,
  MatchCardLikeButton,
  MatchCardPassButton,
  MatchCardSaveButton,
  MatchCardViewProfileButton,
} from "../../../styles/SocialFeed/MatchCard.styles";

interface MatchCardProps {
  item: FeedItem;
  onLike?: (userId: string) => void;
  onPass?: (userId: string) => void;
  onSaveToDeck?: (userId: string, isCurrentlySaved: boolean) => void;
  onViewProfile?: (userId: string) => void;
  isLiked?: boolean;
  isSaved?: boolean;
  /** Already passed on — the X shows red so the action is visibly recorded. */
  isPassed?: boolean;
  // Whether this is the card currently on screen. In a one-card-per-viewport
  // feed only that card should be playing; defaults true so a caller that
  // renders a single card keeps the old behaviour.
  isActive?: boolean;
  // Fetch the video ahead of time for the immediate neighbours, so scrolling
  // doesn't land on a stalled frame. Everything else stays at preload="none",
  // which is what keeps a long feed from downloading every video at once.
  shouldPreload?: boolean;
  // Which feed this card is in, so the signals it emits record where the
  // engagement happened. Omitted means no view signals are recorded.
  surface?: SignalSurface;
}

export const MatchCard: React.FC<MatchCardProps> = ({
  item,
  onLike,
  onPass,
  onSaveToDeck,
  onViewProfile,
  isLiked = false,
  isSaved = false,
  isPassed = false,
  isActive = true,
  shouldPreload = true,
  surface,
}) => {
  const { content } = item;
  const [justLiked, setJustLiked] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);
  const [justPassed, setJustPassed] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  // When this card became the active one, so leaving it can be reported as
  // time actually spent.
  const activeSince = React.useRef<number | null>(null);

  // Drive playback from whether the card is on screen. autoPlay alone would
  // start every video in the feed at once and never stop the ones scrolled past.
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      // play() rejects when the browser blocks autoplay; that is expected and
      // must not surface as an unhandled rejection.
      video.play().catch(() => { /* autoplay blocked — poster frame remains */ });
    } else {
      video.pause();
      // Rewind so returning to a card restarts the intro rather than resuming
      // wherever the viewer happened to leave it.
      video.currentTime = 0;
    }
  }, [isActive, content.media]);

  // Impression on arrival, dwell on departure. The dwell is what distinguishes
  // a profile someone studied from one they flicked past, and trackDwell turns
  // a very short one into a quick_skip rather than weak interest.
  React.useEffect(() => {
    if (!surface) return;

    if (isActive) {
      activeSince.current = Date.now();
      signalsService.track({
        signalType: 'card_impression',
        targetUserId: item.userId,
        surface,
        itemId: item.id,
      });
    } else if (activeSince.current !== null) {
      signalsService.trackDwell('card_dwell', Date.now() - activeSince.current, {
        targetUserId: item.userId,
        surface,
        itemId: item.id,
      });
      activeSince.current = null;
    }

    // Unmounting while active still counts — leaving the tab mid-card is a
    // departure like any other, and dropping it would bias dwell upward by
    // only ever recording the cards people scrolled away from.
    return () => {
      if (activeSince.current !== null) {
        signalsService.trackDwell('card_dwell', Date.now() - activeSince.current, {
          targetUserId: item.userId,
          surface,
          itemId: item.id,
        });
        activeSince.current = null;
      }
    };
  }, [isActive, surface, item.userId, item.id]);

  // Reset animation flags after animation completes
  React.useEffect(() => {
    if (justLiked) {
      const timer = setTimeout(() => setJustLiked(false), 400);
      return () => clearTimeout(timer);
    }
  }, [justLiked]);

  React.useEffect(() => {
    if (justSaved) {
      const timer = setTimeout(() => setJustSaved(false), 500);
      return () => clearTimeout(timer);
    }
  }, [justSaved]);

  React.useEffect(() => {
    if (justPassed) {
      const timer = setTimeout(() => setJustPassed(false), 400);
      return () => clearTimeout(timer);
    }
  }, [justPassed]);

  // Get compatibility score color
  const getCompatibilityColor = (score: number): string => {
    if (score >= 90) return "#2ecc71"; // Green
    if (score >= 80) return "#f39c12"; // Orange
    if (score >= 70) return "#e74c3c"; // Red
    return "#95a5a6"; // Gray
  };

  const handleLike = () => {
    setJustLiked(true);
    onLike?.(item.userId);
  };

  const handlePass = () => {
    // Same immediate feedback the heart and the deck button give. `justPassed`
    // drives the one-off animation; `isPassed` comes back from the container
    // once the swipe is actually recorded, so the colour reflects the write
    // rather than the click.
    setJustPassed(true);
    onPass?.(item.userId);
  };

  const handleSaveToDeck = () => {
    setJustSaved(true);
    onSaveToDeck?.(item.userId, isSaved);
  };

  const handleViewProfile = () => {
    onViewProfile?.(item.userId);
  };

  const compatibilityScore = content.compatibilityScore || 0;
  const compatibilityColor = getCompatibilityColor(compatibilityScore);

  return (
    <MatchCardContainer>
      {/* Photo Section - 100% of card */}
      <MatchCardPhoto $photoUrl={item.avatar}>
        {/* Video Background */}
        {content.media?.type === 'video' && content.media.url && (
          <video
            ref={videoRef}
            src={Array.isArray(content.media.url) ? content.media.url[0] : content.media.url}
            // The profile's main photo shows until the video has frames, so the
            // card is never blank while it loads.
            poster={content.media.thumbnail || item.avatar || undefined}
            preload={shouldPreload ? 'auto' : 'none'}
            loop
            muted
            playsInline
            onPlay={() => {
              if (surface) {
                signalsService.track({
                  signalType: 'video_play',
                  targetUserId: item.userId,
                  surface,
                  itemId: item.id,
                });
              }
            }}
            // The card loops, so a completed pass is detected here rather than
            // at 'ended', which a looping video never fires. The first
            // completion means they watched it through; a later one means they
            // stayed for a second viewing, which says more than the first.
            //
            // timeupdate fires several times a second, so the pass is latched
            // and only cleared once playback has wrapped back to the start —
            // otherwise the last third of a second would emit a handful of
            // completions and inflate the signal.
            onTimeUpdate={(e) => {
              if (!surface) return;
              const el = e.currentTarget;
              if (!el.duration || !Number.isFinite(el.duration)) return;

              const nearEnd = el.currentTime >= el.duration - 0.35;
              const latched = el.dataset.completed === '1';

              if (nearEnd && !latched) {
                el.dataset.completed = '1';
                const loops = (el.dataset.loops ? Number(el.dataset.loops) : 0) + 1;
                el.dataset.loops = String(loops);
                signalsService.track({
                  signalType: loops > 1 ? 'video_replay' : 'video_complete',
                  targetUserId: item.userId,
                  surface,
                  itemId: item.id,
                  metadata: { loops, durationSeconds: Math.round(el.duration) },
                });
              } else if (!nearEnd && latched && el.currentTime < 1) {
                // Wrapped around: ready to count the next pass.
                el.dataset.completed = '0';
              }
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 5 // Ensure video sits above background image
            }}
          />
        )}

        {/* Right Action Buttons */}
        <MatchCardRightActions>
          <MatchCardLikeButton
            $isLiked={isLiked}
            $justLiked={justLiked}
            onClick={handleLike}
            aria-label={isLiked ? "Unlike" : "Like"}
          >
            <Heart
              size={24}
              fill={isLiked ? "white" : "none"}
              stroke="white"
              strokeWidth={isLiked ? 0 : 2.5}
            />
          </MatchCardLikeButton>

          <MatchCardPassButton
            $isPassed={isPassed}
            $justPassed={justPassed}
            onClick={handlePass}
            aria-label={isPassed ? "Passed" : "Pass"}
          >
            <X
              size={24}
              stroke="white"
              strokeWidth={2.5}
            />
          </MatchCardPassButton>

          <MatchCardSaveButton
            $isSaved={isSaved}
            $justSaved={justSaved}
            onClick={handleSaveToDeck}
            aria-label={isSaved ? "Remove from Deck" : "Save to Deck"}
          >
            {isSaved ? "✓" : "🃏"}
          </MatchCardSaveButton>

          <MatchCardViewProfileButton
            onClick={handleViewProfile}
            aria-label="View Profile"
          >
            👤
          </MatchCardViewProfileButton>
        </MatchCardRightActions>

        {/* Bottom Info Overlay */}
        <PhotoOverlay>
          <MatchCardBadge $color={compatibilityColor}>
            {compatibilityScore}% Match
          </MatchCardBadge>
          <MatchCardName>{item.username}</MatchCardName>
          <MatchCardInfo>
            {item.metadata?.age && `${item.metadata.age} • `}
            {item.metadata?.distance && `${item.metadata.distance} miles away`}
            {item.metadata?.location && ` • ${item.metadata.location}`}
          </MatchCardInfo>
        </PhotoOverlay>
      </MatchCardPhoto>
    </MatchCardContainer>
  );
};

